import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { IceCream, Play, CheckCircle2, Clock, Sparkles, Trophy, Flame } from 'lucide-react';
import { SUNDAE_GAME_LABELS } from '../../services/sundaeApi';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Daily Devotional Sundae — pinned at the top of the Games tab.
 * Three render states keyed off `state.next_game` and `state.completed_at`:
 *  - Not started      → "Start Sundae" CTA
 *  - In progress      → "Resume Sundae" CTA + progress bar
 *  - Completed today  → Final summary chip + view-card link
 *
 * Pure presentation; the parent (`GamesTab`) owns state + start/launch wiring.
 *
 * v40: optional `streak` prop — when provided shows a Flame chip with the
 * current consecutive-day streak in the header.
 */
const SundaeStartCard = ({ language = 'en', state, streak, onStart, onView }) => {
  const { t } = useLanguage();
  if (!state) return null;

  const total = state.games?.length || 5;
  const done = state.games?.filter((g) => g.completed).length || 0;
  const percent = Math.round((done / total) * 100);
  const isComplete = !!state.completed_at;
  const inProgress = !isComplete && (state.started_at || done > 0);
  const elapsedMin = state.total_elapsed_ms
    ? Math.floor(state.total_elapsed_ms / 60000)
    : null;
  const elapsedSec = state.total_elapsed_ms
    ? Math.floor((state.total_elapsed_ms % 60000) / 1000)
    : null;

  return (
    <Card
      className="overflow-hidden border-0 shadow-xl"
      data-testid="sundae-start-card"
    >
      <div className="relative bg-gradient-to-br from-purple-600 via-fuchsia-600 to-pink-600 text-white p-5">
        <div className="absolute inset-0 opacity-10 mix-blend-overlay pointer-events-none">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white blur-3xl" />
          <div className="absolute -left-12 bottom-0 w-32 h-32 rounded-full bg-yellow-200 blur-2xl" />
        </div>

        <div className="relative">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <IceCream className="w-5 h-5" />
                <Badge className="bg-white/15 text-white border-white/30 text-[10px] uppercase tracking-widest">
                  {t('games.sundae.newBadge')}
                </Badge>
              </div>
              <h3 className="text-2xl font-bold mt-2">
                {t('games.sundae.title')}
              </h3>
              <p className="text-sm opacity-90 mt-1 max-w-md">
                {t('games.sundae.subtitle')}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {isComplete && (
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-yellow-300 text-purple-900 text-[10px] font-bold uppercase tracking-wide">
                  <Trophy className="w-3 h-3" />
                  {t('games.sundae.doneToday')}
                </div>
              )}
              {streak?.streak_days > 0 && (
                <div
                  className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-400/95 text-orange-950 text-[10px] font-bold uppercase tracking-wide"
                  data-testid="sundae-streak-chip"
                  title={
                    streak.longest_streak > streak.streak_days
                      ? `${t('games.sundae.best')}: ${streak.longest_streak}`
                      : ''
                  }
                >
                  <Flame className="w-3 h-3" />
                  {streak.streak_days} {t('games.sundae.dayUnit')}{streak.streak_days !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* Sequence preview */}
          <div className="flex items-center gap-1.5 mt-4 flex-wrap">
            {(state.order || []).map((g, i) => {
              const status = state.games.find((s) => s.game === g);
              const completed = !!status?.completed;
              return (
                <div
                  key={g}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border ${
                    completed
                      ? 'bg-emerald-300/95 text-emerald-900 border-emerald-100'
                      : 'bg-white/10 text-white border-white/20'
                  }`}
                  data-testid={`sundae-step-${g}`}
                >
                  <span className="opacity-70 tabular-nums">{i + 1}.</span>
                  {completed ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : null}
                  {SUNDAE_GAME_LABELS[g]?.[language] || g}
                </div>
              );
            })}
          </div>

          {/* CTA / progress */}
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            {!isComplete ? (
              <Button
                onClick={onStart}
                size="lg"
                className="bg-white text-purple-700 hover:bg-white/95 font-bold shadow-md"
                data-testid={inProgress ? 'sundae-resume-btn' : 'sundae-start-btn'}
              >
                <Play className="w-4 h-4 mr-2 fill-current" />
                {inProgress
                  ? t('games.sundae.resume', { done, total })
                  : t('games.sundae.start')}
              </Button>
            ) : (
              <Button
                onClick={onView}
                size="lg"
                className="bg-white text-purple-700 hover:bg-white/95 font-bold shadow-md"
                data-testid="sundae-view-card-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('games.sundae.viewCard')}
              </Button>
            )}

            {isComplete && (
              <div className="flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1 font-bold tabular-nums">
                  <Trophy className="w-4 h-4 text-yellow-300" />+{state.sundae_score}
                </span>
                {elapsedMin !== null && (
                  <span className="flex items-center gap-1 opacity-90 tabular-nums">
                    <Clock className="w-3.5 h-3.5" />
                    {elapsedMin}:{String(elapsedSec).padStart(2, '0')}
                  </span>
                )}
              </div>
            )}

            {!isComplete && (
              <div className="flex-1 min-w-[140px] max-w-[260px]">
                <div className="h-1.5 rounded-full bg-white/20 overflow-hidden">
                  <div
                    className="h-full bg-yellow-300 transition-all duration-500"
                    style={{ width: `${percent}%` }}
                    data-testid="sundae-progress-bar"
                  />
                </div>
                <p className="text-[10px] mt-1 opacity-80">
                  {done}/{total} {t('games.sundae.completed')}
                  {state.base_score > 0 && (
                    <> · +{state.base_score} pts</>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SundaeStartCard;
